import { useState, type FormEvent } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button } from '@heroui/react';
import { useStepUp } from '@/features/mfa/hooks';
import { useCreateOwnerWebmailSession } from '@/features/platform/hooks';
import { beginOwnerInboxHandoff, submitOwnerInboxHandoff } from '@/features/platform/ownerInboxHandoff';
import type { Mailbox } from '@/features/mailboxes/types';
import { apiErrorMessage } from '@/api/errors';

const SIX = /^\d{6}$/;

/**
 * Opening a customer's inbox needs an authenticator code proven in the last 15 minutes
 * (organization-service answers AUTH_STEP_UP_REQUIRED otherwise). The code is confirmed and the
 * inbox opened from this one click, so the inbox tab is not treated as a scripted pop-up.
 */
export function OwnerInboxStepUpModal({ mailbox, onClose }: { mailbox: Mailbox | null; onClose: () => void }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const stepUp = useStepUp();
  const ownerWebmail = useCreateOwnerWebmailSession();
  const close = () => { setCode(''); setError(undefined); onClose(); };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!mailbox) return;
    const c = code.replace(/\s+/g, '');
    if (!SIX.test(c)) { setError('Enter the 6-digit code from your authenticator app.'); return; }
    const inboxWindow = beginOwnerInboxHandoff();
    if (!inboxWindow) { setError('Your browser blocked the inbox tab. Allow pop-ups for Nifty Hosting and try again.'); return; }
    try {
      await stepUp.mutateAsync(c);
    } catch (err) {
      inboxWindow.close();
      setCode('');
      setError(apiErrorMessage(err, 'That code is not valid. Try the next one.'));
      return;
    }
    try {
      submitOwnerInboxHandoff(inboxWindow, await ownerWebmail.mutateAsync(mailbox));
      close();
    } catch (err) {
      inboxWindow.close();
      setError(apiErrorMessage(err, 'Could not open this inbox'));
    }
  };

  return (
    <Modal isOpen={!!mailbox} onClose={close} size="sm">
      <ModalContent>
        <ModalHeader className="text-fg">Confirm it’s you</ModalHeader>
        <ModalBody>
          <p className="text-sm text-fg-muted">
            Opening <span className="font-medium text-fg">{mailbox?.address}</span> needs a code from your authenticator app.
          </p>
          <form id="owner-inbox-step-up" onSubmit={submit} className="mt-2" noValidate>
            <label htmlFor="owner-inbox-step-up-code" className="mb-1 block text-sm font-medium text-fg">6-digit code</label>
            <input id="owner-inbox-step-up-code" value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code"
              inputMode="numeric" spellCheck={false} placeholder="123456" autoFocus
              aria-invalid={!!error} aria-describedby={error ? 'owner-inbox-step-up-err' : undefined}
              className="h-11 w-full rounded-lg border border-border bg-surface px-3 font-mono text-base tracking-widest text-fg focus:border-brand focus:ring-2 focus:ring-brand/30" />
            {error && <p id="owner-inbox-step-up-err" role="alert" className="mt-1 text-sm text-danger">{error}</p>}
          </form>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={close}>Cancel</Button>
          <Button color="primary" type="submit" form="owner-inbox-step-up" isLoading={stepUp.isPending || ownerWebmail.isPending}>
            Confirm and open inbox
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
